import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AUTH_QUERY_KEY } from '@/lib/auth';
import { LANGUAGE_OPTIONS, setAppLanguage } from '@/locales/i18n';
import type { User } from '@/domain/authentication';
import {
  fetchUserProfile,
  fetchUserCompanyContacts,
  fetchWebAppLanguages,
  updateUserProfile,
  changeUserPassword,
  setMyLanguage,
  type UpdateUserRequest,
  type ChangePasswordRequest,
  type WebAppLanguage,
} from '../api/user';

export const userKeys = {
  all: ['user'] as const,
  profile: (userId: string) => [...userKeys.all, 'profile', userId] as const,
  contacts: (userId: string) => [...userKeys.all, 'contacts', userId] as const,
  webAppLanguages: () => [...userKeys.all, 'webAppLanguages'] as const,
  myInvites: () => [...userKeys.all, 'myInvites'] as const,
};

export { useMyInvites, useAcceptInvite, useDeclineInvite } from './useInvites';

export function useUserProfile(userId: string) {
  return useQuery({
    queryKey: userKeys.profile(userId),
    queryFn: () => fetchUserProfile(userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!userId,
  });
}

export function useUserCompanyContacts(userId: string) {
  return useQuery({
    queryKey: userKeys.contacts(userId),
    queryFn: () => fetchUserCompanyContacts(userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!userId,
  });
}

export function useWebAppLanguages(enabled = true) {
  return useQuery({
    queryKey: userKeys.webAppLanguages(),
    queryFn: fetchWebAppLanguages,
    select: keepShippedLanguages,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled,
  });
}

export function useUpdateUserProfile(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserRequest) => updateUserProfile(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.profile(userId) });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => changeUserPassword(data),
  });
}

export function useSetMyLanguage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ language }: { language: WebAppLanguage }) =>
      setMyLanguage({ languageId: language.id }),
    onSuccess: async (_, { language }) => {
      queryClient.setQueryData<User | null>(AUTH_QUERY_KEY, (currentUser) => {
        if (!currentUser) {
          return currentUser;
        }

        return {
          ...currentUser,
          language,
        };
      });

      try {
        await setAppLanguage(language.isoCode);
      } catch (error) {
        console.error('Failed to apply language change to UI:', error);
      }
    },
  });
}

// Offers only languages the UI ships, in LANGUAGE_OPTIONS order and label, so it matches the logged-out switch
function keepShippedLanguages(languages: WebAppLanguage[]): WebAppLanguage[] {
  return LANGUAGE_OPTIONS.flatMap((option) => {
    const backendLanguage = languages.find((language) => language.isoCode === option.id);
    return backendLanguage ? [{ ...backendLanguage, name: option.label }] : [];
  });
}
